import api from './api';

/**
 * Attendance Summary — client for the daily exception digest endpoints.
 * Same DTO shapes as the backend service so the FE renders whatever the
 * server sends without a second interpretation layer.
 */

export type InformationTypeChip = 'INFORMED' | 'UNINFORMED';

export interface ExceptionPerson {
  id: string;
  name: string;
  identifier: string;                    // roll number for students, userCode for employees
  informationType?: InformationTypeChip; // absent/late/half-day only
  remarks?: string;
  leaveType?: string;                    // populated for LEAVE rows only
}

export interface ExceptionBuckets {
  absent:  ExceptionPerson[];
  late:    ExceptionPerson[];
  halfDay: ExceptionPerson[];
  leave:   ExceptionPerson[];
}

export interface SummaryGroup {
  key: string;
  primaryLabel: string;
  secondaryLabel?: string;
  totalRoster: number;
  presentCount: number;
  exceptionCount: number;
  notMarkedCount: number;
  allPresent: boolean;
  notMarked: boolean;
  exceptions: ExceptionBuckets;
}

export interface AttendanceSummaryResponse {
  school: { _id: string; name: string };
  date: string;
  session?: { _id: string; name: string };
  groups: SummaryGroup[];
  totals: { rosters: number; present: number; exceptions: number; notMarked: number };
}

export interface StudentSummaryFilter {
  date: string;                          // YYYY-MM-DD
  classIds?: string[];
  sectionInstanceIds?: string[];
}

const idListParam = (ids?: string[]) => (ids && ids.length > 0 ? ids.join(',') : undefined);

export const attendanceSummaryService = {
  formatDateForAPI(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  },

  async getStudentSummary(filter: StudentSummaryFilter): Promise<AttendanceSummaryResponse> {
    const response = await api.get('/attendance-summary/students', {
      params: {
        date: filter.date,
        classIds: idListParam(filter.classIds),
        sectionInstanceIds: idListParam(filter.sectionInstanceIds)
      }
    });
    return response.data.data;
  },

  async getEmployeeSummary(date: string): Promise<AttendanceSummaryResponse> {
    const response = await api.get('/attendance-summary/employees', { params: { date } });
    return response.data.data;
  }
};
